import type { FilePreviewType } from './UploadReceiptCard';
import type { FC } from 'react';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Skeleton, Stack, styled, Typography } from '@mui/material';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { Box, PdfFileIcon, usePdfObjectUrl } from '@pflegenavi/web-components';
import { useTheme } from '@mui/material/styles';
import { truncateFileNameIfNeeded } from './truncateFileNameIfNeeded';

interface ImagePreviewContainerProps {
  currentImageUrl: string;
  file: FilePreviewType;
  imageUrlToTypeMap: UrlTypeNameMapping;
  /**
   * Render PDFs inline instead of showing the icon placeholder (ENG-2295).
   * Turn it off where the preview column is too narrow to read an A4 page -
   * there the placeholder plus the enlarge dialog is more useful.
   */
  inlinePdfPreview?: boolean;
}

export const ImagePreviewContainer: FC<ImagePreviewContainerProps> = ({
  currentImageUrl,
  imageUrlToTypeMap,
  file,
  inlinePdfPreview = true,
}) => {
  // In-place point-zoom toggle. Zooming doubles the layout width rather than
  // applying a CSS transform: a transform leaves the layout box unchanged, so
  // whatever scales past the container's top or left edge is overflow the
  // browser cannot scroll to (ENG-2545).
  const [pointZoomed, setPointZoomed] = useState(false);

  // Reset zoom when navigating to a different image; render-time reset so the
  // new image never paints zoomed.
  const [prevImageUrl, setPrevImageUrl] = useState(currentImageUrl);
  if (prevImageUrl !== currentImageUrl) {
    setPrevImageUrl(currentImageUrl);
    setPointZoomed(false);
  }

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (pointZoomed) {
      setPointZoomed(false);
      return;
    }
    const image = e.currentTarget;
    const scroller = image.parentElement;
    const rect = image.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Commit the doubled width synchronously so the scroll below is not
    // clamped to the pre-zoom extent; then keep the clicked point under the
    // cursor.
    flushSync(() => setPointZoomed(true));
    if (scroller) {
      const scrollerRect = scroller.getBoundingClientRect();
      scroller.scrollLeft = 2 * x - (e.clientX - scrollerRect.left);
      scroller.scrollTop = 2 * y - (e.clientY - scrollerRect.top);
    }
  };

  const { t } = useTranslation();

  const isLoading = imageUrlToTypeMap.get(currentImageUrl) === undefined;

  const fileWithType = imageUrlToTypeMap.get(currentImageUrl);

  const isPdf = fileWithType?.type === 'application/pdf';
  const isImage = fileWithType?.type.startsWith('image/') ?? false;
  const fileName = fileWithType?.name ?? file?.name ?? 'unknown';
  // The blob was already fetched to sniff the content type, so rendering the
  // PDF from it costs no second download.
  const pdfUrl = usePdfObjectUrl(
    isPdf && inlinePdfPreview ? fileWithType?.data : undefined
  );

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 340,
        minHeight: 140,
      }}
    >
      {isLoading && (
        <Skeleton variant="rectangular" width="100%" height={324} />
      )}
      {!isLoading && (
        <picture
          style={{
            position: 'relative',
            // A tall or zoomed image is larger than the box this container is
            // given, so it scrolls here. PDFs never do - the inline viewer is
            // sized to 100% height and scrolls its own pages.
            overflow: 'auto',
            display: 'block',
            width: '100%',
            flex: 1,
            minHeight: 0,
          }}
        >
          <source
            src={
              typeof file === 'string'
                ? file
                : file
                ? file.preview ?? undefined
                : ''
            }
            style={{ zIndex: -1, borderRadius: 4 }}
          />
          {!isPdf && !isImage ? (
            <FileTypeNotSupportedCard
              text={t('common.file-type-not-supported', {
                fileType: fileWithType?.type,
              })}
            />
          ) : isPdf && !inlinePdfPreview ? (
            <PdfDownloadCard fileUrl={currentImageUrl} fileName={fileName} />
          ) : isPdf ? (
            // Rendered inline (ENG-2295) so the receipt can be read while
            // filling in the form, without opening the enlarge dialog first.
            // It fills the height its container is given rather than holding
            // A4 proportions, so the dialog's spare vertical space goes to the
            // receipt; the built-in viewer fits the page to whatever it gets.
            // The blob body can still be downloading after the type mapping
            // resolved, so the skeleton covers that gap; the children are the
            // <object> fallback for browsers that render no inline PDF.
            pdfUrl ? (
              <object
                type="application/pdf"
                data={pdfUrl}
                aria-label={fileName}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  borderRadius: 4,
                }}
              >
                <PdfDownloadCard
                  fileUrl={currentImageUrl}
                  fileName={fileName}
                />
              </object>
            ) : (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )
          ) : (
            <img
              alt=""
              src={currentImageUrl}
              decoding="async"
              onClick={handleImageClick}
              style={{
                zIndex: -1,
                borderRadius: 4,
                display: 'block',
                // The global reset caps img at max-width 100%, which would
                // silently swallow the doubled width.
                maxWidth: 'none',
                width: pointZoomed ? '200%' : '100%',
                cursor: pointZoomed ? 'zoom-out' : 'zoom-in',
              }}
            />
          )}
        </picture>
      )}
    </Stack>
  );
};

export const ImageActionButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    borderRadius: 6,
    color: 'black',
    bgcolor: 'rgb(255,255,255, 0.5)',
    '&:hover': {
      bgcolor: 'rgb(255,255,255, 0.7)',
    },
  })
);

interface FileTypeNotSupportedCardProps {
  text: string;
}

const FileTypeNotSupportedCard: FC<FileTypeNotSupportedCardProps> = ({
  text,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        height: 213,
        backgroundColor: theme.palette.grey['200'],
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Typography
        sx={{
          width: '50%',
          textAlign: 'center',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'absolute',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

interface PdfDownloadCardProps {
  fileUrl: string;
  fileName: string;
}

const PdfDownloadCard: FC<PdfDownloadCardProps> = ({ fileUrl, fileName }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const cleanedFileName = truncateFileNameIfNeeded(fileName);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        backgroundColor: theme.palette.grey['200'],
        borderRadius: 2,
        width: '100%',
        height: 324,
      }}
    >
      <a href={fileUrl} target="_blank" rel="noreferrer">
        <img
          alt="Click to download PDF file"
          src={PdfFileIcon}
          style={{ height: 71, display: 'block' }}
        />
      </a>
      <Stack
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography color={theme.palette.grey['500']} variant="subtitle2">
          {t('actions.click-icon-pdf')}
        </Typography>
        <Typography color={theme.palette.grey['500']} variant="subtitle2">
          {t('file.file-name') + ': ' + cleanedFileName}
        </Typography>
      </Stack>
    </Stack>
  );
};
